import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { App, Dropdown } from 'antd'
import type { MenuProps } from 'antd'

import { configManager } from '@/Core/Managers/Config'
import { StringInput } from '../../Inputs/StringInput'
import { useFormContext, useFormField } from '../../Providers'
import { FormFieldContextProvider, useFormFieldContext } from '../../Providers/FormFieldContextProvider'
import { BaseFieldStore } from '../../Stores/BaseFieldStore'
import { FieldType } from '../../Stores/Types/FieldTypeEnum'
import { DefaultFieldTemplate } from '../../Templates/DefaultFieldTemplate'
import { DefaultViewFieldTemplate } from '../../Templates/DefaultViewFieldTemplate'
import { FormMultipleField } from '../FormMultipleField'
import { restClient } from '@/Core/Services/RestClientService'

import type { AnyFieldProps } from '../../Contracts'
import { useCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'

/**
 * Інтерфейс для опції статусу
 */
interface StatusOption {
    value: string
    label: string
    color?: string
}

/**
 * Інтерфейс для кольорів статусу
 */
interface StatusColors {
    bgColor: string
    borderColor: string
    textColor: string
}

/**
 * Props для компонента StatusDropdownView
 */
interface StatusDropdownViewProps {
    currentOption: StatusOption
    options: StatusOption[]
    colors: StatusColors
    generateColors: (color: string) => StatusColors
    item: any,
    field: any
    crudContext?: any
}

/**
 * Компонент для відображення редагованого статусу з dropdown меню (для режиму перегляду)
 */
const StatusDropdownView: React.FC<StatusDropdownViewProps> = ({
    currentOption,
    options,
    colors: initialColors,
    generateColors,
    item,
    field,
    crudContext,
}) => {
    const { notification, modal } = App.useApp()
    const [selectedOption, setSelectedOption] = useState<StatusOption>(currentOption)
    const [colors, setColors] = useState<StatusColors>(initialColors)
    
    // Формуємо елементи меню для Dropdown
    const menuItems: MenuProps['items'] = options.map((option) => {
        const optionColors = generateColors(option.color || '#8C8C8C')
        return {
            key: option.value,
            style: { padding: '2px 4px' },
            label: (
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        backgroundColor: optionColors.bgColor,
                        border: `1px solid ${optionColors.borderColor}`,
                        color: optionColors.textColor,
                        borderRadius: '8px',
                        padding: '8px 16px',
                        height: '24px',
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '24px',
                        boxSizing: 'border-box',
                    }}
                >
                    {option.label}
                </span>
            ),
            onClick: async () => {
                // Показуємо модальне підтвердження якщо є конфіг
                if (
                    Array.isArray(field?.definition?.confirmOnValues) &&
                    field.definition.confirmOnValues.includes(option.value) &&
                    field?.definition?.confirmText
                ) {
                    const confirmed = await new Promise<boolean>((resolve) => {
                        modal.confirm({
                            title: 'Підтвердження',
                            content: field.definition.confirmText,
                            okText: 'Так',
                            cancelText: 'Скасувати',
                            onOk: () => resolve(true),
                            onCancel: () => resolve(false),
                        })
                    })
                    if (!confirmed) return
                }

                const itemIdRaw = item?.['@id']
                if (!itemIdRaw || typeof itemIdRaw !== 'string') {
                    notification.error({
                        duration: 5,
                        title: 'Помилка',
                        description: 'Не вдалося визначити ідентифікатор запису',
                        placement: 'bottomRight',
                    })
                    return
                }
                const itemId: string = itemIdRaw
                
                const changeUrl = typeof field?.definition?.getChangeUrl === 'function' ? field.definition.getChangeUrl({
                    currentOption,
                    options,
                    colors: initialColors,
                    generateColors,
                    item,
                    field
                }) : itemId 
                
                const patchPromise = restClient.patch(changeUrl, {
                    json: {
                        status: option.value,
                    }
                })
                
                if (!patchPromise) {
                    notification.error({
                        duration: 5,
                        title: 'Помилка',
                        description: 'Не вдалося виконати запит',
                        placement: 'bottomRight',
                    })
                    return
                }
                
                void patchPromise
                    .then(() => {
                        // Оновлюємо локальний стан після успішного запиту
                        setSelectedOption(option)
                        setColors(generateColors(option.color || '#8C8C8C'))
                        
                        notification.success({
                            duration: 3,
                            title: 'Успішно',
                            description: `Статус змінено на "${option.label}"`,
                            placement: 'bottomRight',
                        })

                        // Перезавантажуємо список і закриваємо карточку після успішної зміни статусу
                        crudContext?.listCrudContext?.listContext?.actions?.load?.(true)
                        crudContext?.closeResourceDetail?.()
                    })
                    .catch((error: any) => {
                        notification.error({
                            duration: 5,
                            title: 'Помилка',
                            description: error?.message || 'Не вдалося змінити статус',
                            placement: 'bottomRight',
                        })
                    })
            }
        }
    })

    // Іконка стрілки вниз
    const ArrowIcon = (
        <svg
            width="8"
            height="5"
            viewBox="0 0 8 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginLeft: '8px', flexShrink: 0 }}
        >
            <path
                d="M3.91992 4.5957C3.67246 4.58382 3.40123 4.4627 3.18164 4.24316L0.353516 1.41504C-0.0370083 1.02451 -0.116136 0.469651 0.176758 0.176758C0.469756 -0.115562 1.02375 -0.0367379 1.41406 0.353516L4 2.93945L6.58496 0.353516C6.9754 -0.0366158 7.52943 -0.115862 7.82227 0.176758C8.11516 0.469651 8.03603 1.02451 7.64551 1.41504L4.81738 4.24316C4.59767 4.46288 4.32573 4.58392 4.07812 4.5957H3.91992Z"
                fill={colors.textColor}
            />
        </svg>
    )

    return (
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <span
                className="status-badge-view status-badge-editable"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colors.bgColor,
                    color: colors.textColor,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 400,
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                }}
            >
                {selectedOption.label}
                {ArrowIcon}
            </span>
        </Dropdown>
    )
}

// Единая реализация для всех типов полей
export function FormField({
    name,
    template,
    label,
    type,
    input,
    output,
    view,
    access,
    depField,
    depFn,
    initialValue,
    defaultValue,
    isMultiple,
    isRequired,
    validators,
    inputProps = {},
    isEditable,
    children,
    ignoreParentField = false,
    ...props
}: AnyFieldProps): JSX.Element {

    const crudContext = useCrudResourceContext();

    const FormFieldComponent = observer(() => {
        const formContext = useFormContext()
        const parentFormFieldContext = useFormFieldContext()
        const formConfig = configManager.get('FormConfig')

        // Проверяем наличие обязательных зависимостей
        if (!formContext || !name) return null

        // Определяем путь к полю
        let path: string[] = []
        if (parentFormFieldContext && !ignoreParentField) {
            path = parentFormFieldContext.path || []
        }
        path = [...path, ...(Array.isArray(name) ? name : [name])]
        const fieldPath = path.join('.')

        // Получаем или создаем поле
        // Якщо є батьківський контекст ObjectFieldStore, шукаємо поле в його nestedFields
        let field: BaseFieldStore | undefined
        if (parentFormFieldContext && !ignoreParentField && 'getNestedField' in parentFormFieldContext) {
            const fieldName = Array.isArray(name) ? name[name.length - 1] : name
            field = (parentFormFieldContext as any).getNestedField(fieldName)
        } else {
            field = useFormField(fieldPath)
        }

        if (!field) {
            // Создаем поле через FormStore если его нет
            const fieldDefinition: any = {
                name: fieldPath,
                type: (type as FieldType) || FieldType.STRING,
                // Не задаємо strategy - дозволяємо FieldStoreFactory визначити автоматично
                label,
                defaultValue,
                isRequired: isRequired || false,
                isMultiple: isMultiple || false,
                isEditable: isEditable,
                inputProps,
                ...(depFn && { depFn }),
            }

            // Додаємо fields якщо вони передані через props
            if (props && (props as any).fields) {
                fieldDefinition.fields = (props as any).fields
            }

            // Додаємо options якщо вони передані через props (для SELECT, STATUS та інших полів)
            if (props && (props as any).options) {
                fieldDefinition.options = (props as any).options
            }

            // Используем createFields метод для создания поля
            formContext.createFields({ [fieldPath]: fieldDefinition })
            field = formContext.getField(fieldPath)
        }

        // TODO: ВАЖЛИВО: Не оновлюємо поле під час рендеру, щоб уникнути нескінченного циклу
        // Всі параметри мають бути встановлені під час створення поля

        if (!field) return null

        // Використовуємо useState для збереження результату depFn (видимість поля)
        const [shouldRender, setShouldRender] = useState(true)

        // Виконуємо depFn тільки в режимі редагування
        // У режимі перегляду depFn НЕ викликається, щоб уникнути безкінечного ререндеру
        // Це важливо, бо depFn може викликати field.mergeProps/mergeInputProps, що змінює observable стан
        useEffect(() => {
            const fieldDepFn = depFn || (field as any).depFn
            
            // У режимі перегляду не викликаємо depFn взагалі
            if (formContext.isEditable === false) {
                return
            }
            
            if (typeof fieldDepFn === 'function') {
                // Функція для виконання depFn
                const executeDep = () => {
                    try {
                        const result = fieldDepFn({ formContext, field })
                        // Оновлюємо видимість поля
                        setShouldRender(result !== false)
                        return result
                    } catch (error) {
                        console.error('Error in depFn:', error)
                        return undefined
                    }
                }

                // Викликаємо depFn відразу
                executeDep()

                // Створюємо reaction для відстеження змін
                const dispose = reaction(
                    // Tracker: відстежуємо всі значення форми
                    () => ({ ...formContext.values }),
                    // Effect: виконуємо depFn при зміні значень
                    () => {
                        executeDep()
                    },
                    {
                        // Порівнюємо об'єкти по JSON
                        equals: (a, b) => JSON.stringify(a) === JSON.stringify(b),
                    }
                )

                return () => dispose()
            }
            
            return undefined
        }, [field, formContext, depFn])

        // Перевірка видимості поля
        if (!shouldRender) {
            return null
        }

        // View режим (неедактируемые поля)
        const fieldIsEditable = (field as any).isEditable !== false && (field as any).definition?.isEditable !== false

        if (field.type !== FieldType.FILE && (formContext.isEditable === false || !fieldIsEditable)) {
            // Перевіряємо кастомний view/template з визначення поля ДО будь-яких null-перевірок
            const defView = (field as any).definition?.view
            if (typeof defView === 'function') {
                return (
                    <FormFieldContextProvider value={field}>
                        {defView({ field, formContext, value: field.value })}
                    </FormFieldContextProvider>
                )
            }
            const defViewTemplate = (field as any).definition?.template
            if (defViewTemplate && typeof defViewTemplate !== 'string') {
                const DefViewTemplate = defViewTemplate as React.ComponentType<any>
                return (
                    <FormFieldContextProvider value={field}>
                        <DefViewTemplate field={field} formContext={formContext}>{field.value}</DefViewTemplate>
                    </FormFieldContextProvider>
                )
            }

            if (field.type === FieldType.OBJECT) return null

            let viewValue = field.value || '-'

            // Обработка EditorJS полей (проверяем строковое значение type)
            // const fieldTypeStr = field.type.toString().toLowerCase()
            // if (fieldTypeStr === 'editorjs' && (field as any).props?.fieldHTMLName) {
            //     const __html = formContext.initialValues[(field as any).props.fieldHTMLName]
            //     if (__html) viewValue = <div dangerouslySetInnerHTML={{ __html }}></div>
            // }

            // Обработка связанных объектов (когда value - это объект с @id и title)
            if (typeof viewValue === 'object' && viewValue !== null && !Array.isArray(viewValue)) {
                if (viewValue.title) {
                    viewValue = viewValue.title
                } else if (viewValue['@id']) {
                    viewValue = viewValue['@id']
                }
            }

            // Преобразование массивов в строку
            if (Array.isArray(viewValue)) {
                // Если массив объектов с title, извлекаем title
                if (viewValue.length > 0 && typeof viewValue[0] === 'object' && viewValue[0]?.title) {
                    viewValue = viewValue.map((item: any) => item.title).join(', ')
                } else {
                    viewValue = viewValue.join(', ')
                }
            }

            // Обработка textarea
            if (field.type === FieldType.TEXTAREA) {
                viewValue = <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{viewValue}</pre>
            }

            // Обработка date и datetime полей
            if (field.type === FieldType.DATE || field.type === FieldType.DATETIME) {
                if (viewValue && viewValue !== '-') {
                    const appConfig = configManager.get('AppConfig')
                    const format = field.type === FieldType.DATE 
                        ? ((appConfig as any)?.dateDefaultFormat || 'YYYY-MM-DD')
                        : ((appConfig as any)?.datetimeDefaultFormat || 'YYYY-MM-DD HH:mm')
                    
                    viewValue = dayjs(viewValue).format(format)
                }
            }

            // Обработка select с options
            if (
                field.type === FieldType.SELECT &&
                typeof field.value === 'string' &&
                Array.isArray((field as any).props?.options)
            ) {
                const option = (field as any).props.options.find((opt: any) => opt.value === field.value)
                if (option?.label) viewValue = option.label
            }

            // Обработка select для связанных объектов
            if (
                field.type === FieldType.SELECT &&
                typeof field.value === 'object' &&
                field.value !== null &&
                Array.isArray((field as any).props?.options)
            ) {
                const option = (field as any).props.options.find((opt: any) => opt.value === field.value['@id'])
                if (option?.label) viewValue = option.label
            }

            // Обработка status поля
            if (field.type === FieldType.STATUS) {
                const definition = (field as any).definition
                const options: Array<{ value: string; label: string; color?: string }> = definition?.options || []
                const currentOption = options.find((opt) => opt.value === field.value)
                
                if (currentOption) {
                    // Функція для генерації кольорів під темну тему.
                    // Використовуємо rgba з низькою opacity замість змішування з білим,
                    // щоб фон був коректним і на темному, і на світлому тлі.
                    const generateColors = (color: string) => {
                        const hexToRgb = (hex: string) => {
                            let cleanHex = hex.replace(/^#/, '').substring(0, 6)
                            if (cleanHex.length === 3) {
                                cleanHex = cleanHex.split('').map(c => c + c).join('')
                            }
                            const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex)
                            return result ? {
                                r: parseInt(result[1]!, 16),
                                g: parseInt(result[2]!, 16),
                                b: parseInt(result[3]!, 16)
                            } : { r: 140, g: 140, b: 140 }
                        }

                        const rgb = hexToRgb(color)
                        const bgColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.85)`
                        const borderColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`

                        return { bgColor, borderColor, textColor: '#ffffff' }
                    }

                    const colors = generateColors(currentOption.color || '#8C8C8C')
                    
                    // Перевіряємо чи є доступ до редагування
                    const hasAccess = crudContext?.accessFn?.({
                        accessMode: 'update',
                        accessItem: formContext.initialValues,
                        crudContext,
                    }) === true && field.inputProps?.disabled !== true
                    
                    if (hasAccess) {
                        // Редагований статус з dropdown
                        viewValue = (
                            <StatusDropdownView
                                currentOption={currentOption}
                                options={options}
                                colors={colors}
                                generateColors={generateColors}
                                item={formContext.initialValues}
                                field={field}
                                crudContext={crudContext}
                            />
                        )
                    } else {
                        // Статичний бейдж
                        viewValue = (
                            <span
                                className="status-badge-view"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: colors.bgColor,
                                    color: colors.textColor,
                                    border: `1px solid ${colors.borderColor}`,
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    fontFamily: 'Montserrat',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    boxSizing: 'border-box',
                                }}
                            >
                                {currentOption.label}
                            </span>
                        )
                    }
                } else {
                    viewValue = field.value || '-'
                }
            }

            // Преобразование строк в HTML
            if (!Array.isArray(viewValue) && typeof viewValue === 'string') {
                viewValue = <div dangerouslySetInnerHTML={{ __html: viewValue }}></div>
            }

            // Определяем шаблон для режима просмотра
            let ViewTemplate: React.ComponentType<any> = DefaultViewFieldTemplate
            
            // Получаем ViewTemplate из конфига формы, если он есть
            if (formConfig && (formConfig as any)?.defaultViewFieldTemplate) {
                ViewTemplate = (formConfig as any).defaultViewFieldTemplate
            }
            
            return (
                <FormFieldContextProvider value={field}>
                    <ViewTemplate field={field}>{viewValue}</ViewTemplate>
                </FormFieldContextProvider>
            )
        }

        // Определяем шаблон поля
        let FieldTemplate: React.ComponentType<any> = DefaultFieldTemplate

        // Получаем настройки шаблонов из конфига формы (если есть)
        const formTemplateConfig = (formContext as any).templateConfig || (formContext as any).config
        if (formTemplateConfig?.defaultFieldTemplate) {
            FieldTemplate = formTemplateConfig.defaultFieldTemplate
        }

        if (
            formTemplateConfig?.fieldTemplates &&
            template !== undefined &&
            typeof template === 'string' &&
            formTemplateConfig.fieldTemplates[template] !== undefined
        ) {
            FieldTemplate = formTemplateConfig.fieldTemplates[template]
        }

        if (template !== undefined && typeof template !== 'string') {
            FieldTemplate = template
        } else if (template === undefined) {
            // Fallback: template з визначення поля (з ресурсу)
            const defTemplate = (field as any).definition?.template
            if (defTemplate && typeof defTemplate !== 'string') {
                FieldTemplate = defTemplate as React.ComponentType<any>
            }
        }

        // Определяем Input компонент
        let InputComponent: React.ComponentType<any> = StringInput

        if (formConfig && (formConfig as any)?.fieldTypes && (formConfig as any).fieldTypes[field.type] !== undefined) {
            InputComponent = (formConfig as any).fieldTypes[field.type]
        }

        if (input !== undefined) {
            InputComponent = input
        } else {
            // Fallback: input з визначення поля (з ресурсу)
            const defInput = (field as any).definition?.input
            if (defInput) InputComponent = defInput
        }

        // Обработка множественных полей
        // Використовуємо needsMultipleWrapper для перевірки, чи потрібен FormMultipleField
        if ((field as any).needsMultipleWrapper === true) {
            return (
                <FormFieldContextProvider value={field}>
                    <FieldTemplate field={field} formContext={formContext}>
                        <FormMultipleField field={field} InputComponent={InputComponent} />
                    </FieldTemplate>
                </FormFieldContextProvider>
            )
        }

        // Обработка объектных полей
        if (field.type === FieldType.OBJECT) {
            // Перевіряємо, чи є поле екземпляром ObjectFieldStore
            if (!field || typeof (field as any).getNestedFields !== 'function') {
                return (
                    <div style={{ color: 'red', border: '1px solid red', padding: '10px' }}>
                        <h4>Error: Object field is not ObjectFieldStore</h4>
                        <p>Field name: {field?.name}</p>
                        <p>Field type: {field?.type}</p>
                        <p>Constructor: {field?.constructor?.name}</p>
                    </div>
                )
            }

            // Викликаємо метод правильно, зберігаючи контекст
            const nestedFields = (field as any).getNestedFields()
            return (
                <FormFieldContextProvider value={field}>
                    <FieldTemplate field={field} formContext={formContext}>
                        <div className="form-field-object-nested-fields">
                            {nestedFields &&
                                nestedFields.map((nestedField: any, index: number) => (
                                    <FormField
                                        key={nestedField.name || index}
                                        name={nestedField.name}
                                        {...nestedField.props}
                                    />
                                ))}
                        </div>
                    </FieldTemplate>
                </FormFieldContextProvider>
            )
        }

        // Пользовательский children
        if (children) {
            return <FormFieldContextProvider value={field}>{children}</FormFieldContextProvider>
        }

        // Стандартный рендер поля
        return (
            <FormFieldContextProvider value={field}>
                <FieldTemplate field={field} formContext={formContext}>
                    <InputComponent field={field} />
                </FieldTemplate>
            </FormFieldContextProvider>
        )
    })

    const formContext = useFormContext()
    const parentFormFieldContext = useFormFieldContext()

    // Отслеживаем изменения зависимых полей
    useEffect(() => {
        if (!formContext) return

        const fields = (() => {
            if (Array.isArray(depField)) return depField
            if (typeof depField === 'string') return [depField]
            return []
        })()

        if (fields.length === 0) return

        let path: string[] = []
        if (parentFormFieldContext && !ignoreParentField) {
            path = parentFormFieldContext.path || []
        }
        path = [...path, ...(Array.isArray(name) ? name : [name])]
        const fieldPath = path.join('.')

        const disposers = fields.map((depFieldName) => {
            return reaction(
                () => formContext.getValue(depFieldName),
                () => {
                    formContext.setInitialValue(fieldPath, undefined)
                },
                { equals: (a, b) => JSON.stringify(a) === JSON.stringify(b) }
            )
        })

        return () => {
            disposers.forEach((dispose) => dispose())
        }
    }, [formContext, parentFormFieldContext, name, depField, ignoreParentField])

    return <FormFieldComponent />
}

export default FormField
